import { Box, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip as MuiTooltip, Typography, IconButton } from '@material-ui/core';
import { Print } from '@material-ui/icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import React, { useEffect, useRef } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#ed213d', '#b070d6'];

interface PieAndTableProps {
    data: { nome: string, total: number, color?: string, hide?: boolean }[];
    name?: string;
    title?: string;
}

export default function PieAndTable({ data, name = '', title = '', ...props }: PieAndTableProps) {
    const [empty, setEmpty] = React.useState(false);
    const printRef = useRef<HTMLDivElement>(null)

    const onPrint = async () => {
        if (!printRef.current) return;

        try {
            const element = printRef.current;
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
                unit: 'px',
                format: [canvas.width, canvas.height]
            });

            pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
            pdf.save(`${title || 'relatorio'}.pdf`);
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
        }
    }

    useEffect(() => {
        if (data?.length === 0) {
            setEmpty(true);
        }
        let total = data?.reduce((partial, item) => partial + item.total, 0);
        if (total === 0) {
            setEmpty(true);
        }
    }, [data]);

    let graph = data;
    if (empty) {
        graph = [{ nome: 'Sem resultados', total: 1, color: 'grey', hide: true }];
    }

    if (!data) return null;
    return (
        <Box mt={2}>
            <Box display="flex" alignItems="center" flexDirection="row" justifyContent="space-between">
                <Typography variant="h3">{title}</Typography>
                <Box display="flex" alignItems="center" flexDirection="row">
                    <MuiTooltip title={"Imprimir Gráfico"}>
                        <IconButton onClick={() => onPrint()}>
                            <Print />
                        </IconButton>
                    </MuiTooltip>
                </Box>
            </Box>
            <div ref={printRef}>
                <Grid container spacing={1} alignItems="center" justifyContent="center">
                    <Grid item xs={6}>
                        <ResponsiveContainer width="100%" height={200}>
                            <PieChart width={200} height={200}>
                                <Tooltip />
                                <Legend verticalAlign="middle" align="right" layout="vertical" />
                                <Pie
                                    data={graph}
                                    cx="50%"
                                    cy="50%"
                                    nameKey={'nome'}
                                    valueKey={'total'}
                                    labelLine={false}
                                    outerRadius={100}
                                    fill="#8884d8"
                                    dataKey="total">
                                    {graph?.map((entry, index) => {
                                        if (entry.color) {
                                            return <Cell key={`cell-${index}`} fill={entry.color} />;
                                        }
                                        return <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />;
                                    })}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </Grid>
                    <Grid item xs={6}>
                        <TableContainer component={Paper}>
                            <Table size="small" aria-label="a dense table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{name}</TableCell>
                                        <TableCell align="right">Quantidade</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map(row => {
                                        if (row.hide) {
                                            return null;
                                        }
                                        return (
                                            <TableRow key={row.nome}>
                                                <TableCell component="th" scope="row">
                                                    {row.nome}
                                                </TableCell>
                                                <TableCell align="right">{row.total}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                    <TableRow>
                                        <TableCell component="th" scope="row">
                                            <b>Total</b>
                                        </TableCell>
                                        <TableCell align="right">
                                            <b>
                                                {data.reduce((partial, item) => {
                                                    if (item.hide) {
                                                        return partial;
                                                    }
                                                    return partial + item.total;
                                                }, 0)}
                                            </b>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                </Grid>
            </div>
        </Box>
    );
}
